import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

const CANCEL_REASONS = [
  { value: "not_using_enough_it_enough", label: "Not using it enough" },
  { value: "need_more_time_to_explore", label: "Need more time to explore" },
  { value: "found_alternative", label: "Found an alternative" },
  { value: "not_good_value_for_money", label: "Not good value for money" },
  { value: "missing_features", label: "Missing features" },
  { value: "not_sure_how_to_use_it", label: "Not sure how to use it" },
  { value: "others", label: "Others" },
];

const MIN_OTHER_REASON_LENGTH = 10;
const MAX_OTHER_REASON_LENGTH = 1500;

const pageStyles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(180deg, #f8fafc 0%, #eef2f7 50%, #e8edf5 100%)",
    padding: "32px 16px",
    boxSizing: "border-box",
  },
  container: {
    maxWidth: "760px",
    margin: "0 auto",
  },
  card: {
    background: "#ffffff",
    borderRadius: "18px",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  header: {
    padding: "28px 24px 18px",
    borderBottom: "1px solid #eef2f7",
  },
  title: {
    margin: 0,
    fontSize: "28px",
    lineHeight: 1.2,
    fontWeight: 800,
    color: "#111827",
  },
  body: {
    padding: "24px",
  },
  infoBox: {
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: "14px",
    padding: "18px",
    marginBottom: "22px",
  },
  infoRow: {
    marginBottom: "10px",
    color: "#111827",
    fontSize: "15px",
  },
  sectionTitle: {
    margin: "0 0 14px",
    fontSize: "18px",
    fontWeight: 700,
    color: "#111827",
  },
  helperText: {
    margin: "0 0 16px",
    fontSize: "14px",
    lineHeight: 1.6,
    color: "#6b7280",
  },
  radioList: {
    display: "grid",
    gap: "10px",
    marginBottom: "16px",
  },
  radioCard: {
    border: "1px solid #d1d5db",
    borderRadius: "12px",
    padding: "14px 16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    cursor: "pointer",
    background: "#fff",
  },
  radioInput: {
    marginTop: "2px",
    transform: "scale(1.1)",
  },
  radioLabelWrap: {
    flex: 1,
  },
  radioLabel: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#111827",
  },
  otherReasonWrap: {
    marginTop: "10px",
    marginBottom: "4px",
  },
  otherReasonLabel: {
    display: "block",
    marginBottom: "8px",
    fontWeight: 700,
    color: "#111827",
  },
  textarea: {
    width: "100%",
    minHeight: "120px",
    resize: "vertical",
    borderRadius: "12px",
    border: "1px solid #d1d5db",
    padding: "12px 14px",
    fontSize: "15px",
    lineHeight: 1.5,
    boxSizing: "border-box",
    outline: "none",
  },
  otherReasonHelperRow: {
    marginTop: "6px",
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  otherReasonHelper: {
    fontSize: "13px",
    color: "#6b7280",
  },
  otherReasonHelperOk: {
    color: "#166534",
  },
  otherReasonHelperLimit: {
    color: "#991b1b",
  },
  otherReasonCount: {
    fontSize: "13px",
    color: "#6b7280",
    fontWeight: 600,
  },
  checkboxRow: {
    marginTop: "18px",
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  checkboxInput: {
    marginTop: "3px",
    flexShrink: 0,
    transform: "scale(1.5)",
  },
  checkboxText: {
    fontSize: "14px",
    lineHeight: 1.6,
    color: "#374151",
  },
  buttonRow: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    marginTop: "18px",
  },
  dangerButton: {
    border: "none",
    background: "#dc2626",
    color: "#fff",
    padding: "12px 18px",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "15px",
  },
  message: {
    borderRadius: "12px",
    padding: "14px 16px",
    marginBottom: "18px",
    fontSize: "14px",
    lineHeight: 1.6,
  },
  messageInline: {
    marginTop: "18px",
    marginBottom: "0",
  },
  success: {
    background: "#ecfdf3",
    border: "1px solid #bbf7d0",
    color: "#166534",
  },
  info: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
  },
  danger: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
  },
  error: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
  },
  loadingWrap: {
    minHeight: "70vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#374151",
    fontSize: "16px",
    fontWeight: 600,
  },

  affectedItemsWrap: {
    marginBottom: "22px",
  },
  tableWrapper: {
    overflowX: "auto",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    background: "#fff",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "520px",
  },
  tableHeadCell: {
    textAlign: "left",
    padding: "12px 14px",
    background: "#f8fafc",
    borderBottom: "1px solid #e5e7eb",
    fontSize: "14px",
    fontWeight: 700,
    color: "#111827",
  },
  tableCell: {
    padding: "12px 14px",
    borderBottom: "1px solid #e5e7eb",
    fontSize: "14px",
    color: "#111827",
    verticalAlign: "top",
  },
};

const getFriendlyBrowserTimezoneLabel = (timeZone) => {
  const normalized = String(timeZone || "UTC").trim();

  if (!normalized || normalized.toUpperCase() === "UTC") {
    return "UTC time";
  }

  const parts = normalized.split("/");
  const lastPart = parts[parts.length - 1] || normalized;

  return `${lastPart.replace(/_/g, " ")} time`;
};

const formatDateInBrowserTimezone = (dateValue) => {
  if (!dateValue) return "-";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "-";

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const datePart = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

  const timePart = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "shortOffset",
  }).formatToParts(date);

  const offsetPart =
    parts.find((part) => part.type === "timeZoneName")?.value || "GMT";

  const friendlyLabel = getFriendlyBrowserTimezoneLabel(timeZone);

  return `${datePart}, ${timePart} ${offsetPart} (${friendlyLabel})`;
};

const CancelSubscription = () => {
  const { store } = useParams();
  const [searchParams] = useSearchParams();

  const token = String(searchParams.get("token") || "").trim();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [loadError, setLoadError] = useState("");
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitMessageType, setSubmitMessageType] = useState("");

  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  const [form, setForm] = useState({
    reason: "",
    other_reason: "",
    confirm_cancel: false,
  });

  const selectedReason = useMemo(() => {
    return CANCEL_REASONS.find((item) => item.value === form.reason) || null;
  }, [form.reason]);

  const shouldShowOtherReason = form.reason === "others";

  const otherReasonLength = useMemo(() => {
    return form.other_reason.trim().length;
  }, [form.other_reason]);

  const cancelLinkStatus = String(
    subscriptionInfo?.cancel_link_status || "",
  ).trim();

  const isAlreadyCancelled = cancelLinkStatus === "cancelled";
  const isActive = cancelLinkStatus === "active";

  const formattedExpiresAt = useMemo(() => {
    return formatDateInBrowserTimezone(subscriptionInfo?.expires_at);
  }, [subscriptionInfo?.expires_at]);

  const formattedAutoRenewsAt = useMemo(() => {
    return formatDateInBrowserTimezone(subscriptionInfo?.auto_renews_at);
  }, [subscriptionInfo?.auto_renews_at]);

  const affectedItems = useMemo(() => {
    return Array.isArray(subscriptionInfo?.affected_items)
      ? subscriptionInfo.affected_items
      : [];
  }, [subscriptionInfo?.affected_items]);

  useEffect(() => {
    loadSubscriptionInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, store]);

  const loadSubscriptionInfo = async () => {
    try {
      setIsLoading(true);
      setLoadError("");
      setSubmitMessage("");
      setSubmitMessageType("");

      if (!store || !token) {
        throw new Error("This cancellation link is invalid or has expired.");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/api/v2/stores/${encodeURIComponent(
          store,
        )}/subscriptions?token=${encodeURIComponent(token)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.error?.message ||
            "This cancellation link is invalid or has expired.",
        );
      }

      const payload = data?.payload || null;

      if (!payload) {
        throw new Error("This cancellation link is invalid or has expired.");
      }

      setSubscriptionInfo(payload);
    } catch (error) {
      setLoadError(
        error?.message || "This cancellation link is invalid or has expired.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    if (!token) {
      return "This cancellation link is invalid or has expired.";
    }

    if (!isActive) {
      return "This subscription is no longer available for cancellation from this link.";
    }

    if (!form.reason) {
      return "Please select one cancellation reason.";
    }

    if (form.reason === "others") {
      const otherReason = form.other_reason.trim();

      if (!otherReason) {
        return "Please tell us your reason.";
      }

      if (otherReason.length < MIN_OTHER_REASON_LENGTH) {
        return `Please provide a little more detail for "Others" (at least ${MIN_OTHER_REASON_LENGTH} characters).`;
      }

      if (otherReason.length > MAX_OTHER_REASON_LENGTH) {
        return `Please keep your reason within ${MAX_OTHER_REASON_LENGTH} characters.`;
      }
    }

    if (!form.confirm_cancel) {
      return "Please confirm that you want to cancel this subscription.";
    }

    return "";
  };

  const handleSubmit = async () => {
    try {
      setSubmitMessage("");
      setSubmitMessageType("");

      const error = validateForm();
      if (error) {
        setSubmitMessage(error);
        setSubmitMessageType("error");
        return;
      }

      const confirmed = window.confirm(
        `Are you absolutely sure you want to cancel your subscription to ${
          subscriptionInfo?.plan_name || "this plan"
        }?\n\nOnce your subscription is cancelled, any future re-subscription will be at the prevailing rate.`,
      );

      if (!confirmed) {
        return;
      }

      setIsSubmitting(true);

      const payload = {
        token,
        reason: form.reason,
        reason_label: selectedReason?.label || "",
        other_reason: form.reason === "others" ? form.other_reason.trim() : "",
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/api/v2/stores/${encodeURIComponent(
          store,
        )}/subscriptions/cancel?token=${encodeURIComponent(token)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.error?.message ||
            "Failed to submit subscription cancellation.",
        );
      }

      setSubmitMessage(
        data?.message || "Your subscription has been cancelled successfully.",
      );
      setSubmitMessageType("success");

      setForm({
        reason: "",
        other_reason: "",
        confirm_cancel: false,
      });

      setSubscriptionInfo((prev) => ({
        ...(prev || {}),
        ...(data?.payload || {}),
        cancel_link_status: data?.payload?.cancel_link_status || "cancelled",
      }));
    } catch (error) {
      setSubmitMessage(
        error?.message || "Failed to submit subscription cancellation.",
      );
      setSubmitMessageType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAffectedItemsTable = () => {
    if (!affectedItems.length) return null;

    return (
      <div style={pageStyles.affectedItemsWrap}>
        <p style={pageStyles.helperText}>
          These are your order items under your subscription to{" "}
          {subscriptionInfo?.plan_name || "this plan"} that have not expired
          yet.
        </p>

        <div style={pageStyles.tableWrapper}>
          <table style={pageStyles.table}>
            <thead>
              <tr>
                <th style={pageStyles.tableHeadCell}>#</th>
                <th style={pageStyles.tableHeadCell}>Reference number</th>
                <th style={pageStyles.tableHeadCell}>Expires at</th>
              </tr>
            </thead>
            <tbody>
              {affectedItems.map((item, index) => (
                <tr key={item?._id || index}>
                  <td style={pageStyles.tableCell}>{index + 1}.</td>
                  <td style={pageStyles.tableCell}>
                    {item?.reference_number || item?.order?._id || "-"}
                  </td>
                  <td style={pageStyles.tableCell}>
                    {formatDateInBrowserTimezone(item?.expires_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div style={pageStyles.page}>
        <div style={pageStyles.loadingWrap}>Loading cancellation page...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={pageStyles.page}>
        <div style={pageStyles.container}>
          <div style={pageStyles.card}>
            <div style={pageStyles.body}>
              <div style={{ ...pageStyles.message, ...pageStyles.error }}>
                {loadError}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isAlreadyCancelled) {
    return (
      <div style={pageStyles.page}>
        <div style={pageStyles.container}>
          <div style={pageStyles.card}>
            <div style={pageStyles.header}>
              <h1 style={pageStyles.title}>Subscription has been cancelled</h1>
            </div>

            <div style={pageStyles.body}>
              <div style={{ ...pageStyles.message, ...pageStyles.danger }}>
                Your subscription to{" "}
                {subscriptionInfo?.plan_name || "this plan"} has been cancelled.
                <strong> Auto-renewal is disabled.</strong>
              </div>

              <div style={pageStyles.infoBox}>
                <div style={pageStyles.infoRow}>
                  <strong>Subscription:</strong>{" "}
                  {subscriptionInfo?.plan_name || "-"}
                </div>
                <div style={pageStyles.infoRow}>
                  <strong>Access ends at:</strong> {formattedExpiresAt}
                </div>
              </div>

              {renderAffectedItemsTable()}

              <div style={pageStyles.helperText}>
                Notes:
                <ol>
                  <li>
                    Any future re-subscription to{" "}
                    {subscriptionInfo?.plan_name || "this plan"} will be at the
                    prevailing rate.
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyles.page}>
      <div style={pageStyles.container}>
        <div style={pageStyles.card}>
          <div style={pageStyles.header}>
            <h1 style={pageStyles.title}>Cancel Subscription</h1>
          </div>

          <div style={pageStyles.body}>
            <div style={{ ...pageStyles.message, ...pageStyles.success }}>
              Your subscription to {subscriptionInfo?.plan_name || "this plan"}{" "}
              is currently active.
              <strong> Auto-renewal is enabled.</strong>
            </div>

            <div style={pageStyles.infoBox}>
              <div style={pageStyles.infoRow}>
                <strong>Subscription:</strong>{" "}
                {subscriptionInfo?.plan_name || "-"}
              </div>

              <div style={pageStyles.infoRow}>
                <strong>Access ends at:</strong> {formattedExpiresAt}
              </div>

              {isActive ? (
                <div style={{ ...pageStyles.infoRow, marginBottom: 0 }}>
                  <strong>Auto-renew at:</strong> {formattedAutoRenewsAt}
                </div>
              ) : null}
            </div>

            {renderAffectedItemsTable()}

            <h2 style={pageStyles.sectionTitle}>
              Would you mind sharing why you’re cancelling?
            </h2>
            <p style={pageStyles.helperText}>
              Please select one reason below. Your feedback helps us to improve.
            </p>

            <div style={pageStyles.radioList}>
              {CANCEL_REASONS.map((reason) => {
                const checked = form.reason === reason.value;

                return (
                  <label
                    key={reason.value}
                    style={{
                      ...pageStyles.radioCard,
                      borderColor: checked ? "#2563eb" : "#d1d5db",
                      background: checked ? "#eff6ff" : "#fff",
                      opacity: isSubmitting ? 0.8 : 1,
                    }}
                  >
                    <input
                      type='radio'
                      name='cancel_reason'
                      value={reason.value}
                      checked={checked}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          reason: e.target.value,
                          other_reason:
                            e.target.value === "others"
                              ? prev.other_reason
                              : "",
                        }))
                      }
                      style={pageStyles.radioInput}
                      disabled={isSubmitting}
                    />

                    <div style={pageStyles.radioLabelWrap}>
                      <div style={pageStyles.radioLabel}>{reason.label}</div>
                    </div>
                  </label>
                );
              })}
            </div>

            {shouldShowOtherReason ? (
              <div style={pageStyles.otherReasonWrap}>
                <label style={pageStyles.otherReasonLabel}>Tell us more</label>
                <textarea
                  value={form.other_reason}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      other_reason: e.target.value.slice(
                        0,
                        MAX_OTHER_REASON_LENGTH,
                      ),
                    }))
                  }
                  placeholder='Please share your reason'
                  style={pageStyles.textarea}
                  disabled={isSubmitting}
                  maxLength={MAX_OTHER_REASON_LENGTH}
                />

                <div style={pageStyles.otherReasonHelperRow}>
                  <div
                    style={{
                      ...pageStyles.otherReasonHelper,
                      ...(otherReasonLength >= MIN_OTHER_REASON_LENGTH &&
                      otherReasonLength <= MAX_OTHER_REASON_LENGTH
                        ? pageStyles.otherReasonHelperOk
                        : {}),
                      ...(otherReasonLength >= MAX_OTHER_REASON_LENGTH
                        ? pageStyles.otherReasonHelperLimit
                        : {}),
                    }}
                  >
                    Minimum {MIN_OTHER_REASON_LENGTH} characters.
                  </div>

                  <div style={pageStyles.otherReasonCount}>
                    {otherReasonLength} / {MAX_OTHER_REASON_LENGTH} characters
                  </div>
                </div>
              </div>
            ) : null}

            <label style={pageStyles.checkboxRow}>
              <input
                type='checkbox'
                checked={form.confirm_cancel}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    confirm_cancel: e.target.checked,
                  }))
                }
                disabled={isSubmitting}
                style={pageStyles.checkboxInput}
              />
              <span style={pageStyles.checkboxText}>
                I confirm that I want to cancel my subscription to{" "}
                {subscriptionInfo?.plan_name || "this plan"} and understand that
                any future re-subscription will be at the prevailing rate.
              </span>
            </label>

            {submitMessage ? (
              <div
                style={{
                  ...pageStyles.message,
                  ...pageStyles.messageInline,
                  ...(submitMessageType === "success"
                    ? pageStyles.success
                    : pageStyles.error),
                }}
              >
                {submitMessage}
              </div>
            ) : null}

            <div style={pageStyles.buttonRow}>
              <button
                type='button'
                onClick={handleSubmit}
                style={{
                  ...pageStyles.dangerButton,
                  opacity: isSubmitting ? 0.7 : 1,
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Cancel Subscription"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelSubscription;
